import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { login as oidcLogin } from "./AuthProvider";
import { storeLoginMethod } from "../session/utils/userSessionStorage";
import { isMyGovSkipEnabled } from "./authConfig";
import { getSessionParams, storeSessionParams } from "../session/utils/sessionStorage";
import { apiFetch } from "../../shared/utils/http";
import { logger } from "../../shared/utils/logger.client";
import { useTranslation } from "react-i18next";
import useSessionTimer from "../session/hooks/useSessionTimer";

const AUTH_COMPLETING_KEY = "authCompleting";

const ProtectedRoute: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { startTimer } = useSessionTimer();

  useEffect(() => {
    let cancelled = false;
    let retryTimer: number | null = null;

    const cleanup = () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const checkAuth = async () => {
      if (cancelled) return;

      logger.debug("[ProtectedRoute] Starting auth check...");

      const authCompleting = sessionStorage.getItem(AUTH_COMPLETING_KEY) === "1";
      if (authCompleting) {
        logger.debug("[ProtectedRoute] Auth completing — holding and retrying soon...");
        setIsAuthenticated(null);

        retryTimer = window.setTimeout(() => {
          void checkAuth();
        }, 400);

        return;
      }

      storeSessionParams();
      const storedParams = getSessionParams();

      if (!storedParams?.sessionId || !storedParams?.returnUrl || !storedParams?.returnMethod) {
        logger.warn("[ProtectedRoute] Missing required session params:", storedParams);
        navigate("/error", {
          state: {
            errorCode: "400",
            errorMessageKey: "protectedRoute.missingParams",
          },
          replace: true,
        });
        return;
      }

      try {
        const res = await apiFetch(
          "/session/status",
          { credentials: "include" },
          { skipLogoutOn401: true }
        );

        if (cancelled) return;

        const data = await res.json();
        logger.debug("[ProtectedRoute] Session status:", data);

        if (data?.authenticated) {
          if (!localStorage.getItem("requestStartTime")) {
            logger.debug("[ProtectedRoute] No requestStartTime found. Starting timer.");
            await startTimer();
          }
          setIsAuthenticated(true);
          return;
        }

        const skipMyGov = await isMyGovSkipEnabled();
        if (cancelled) return;

        if (skipMyGov) {
          storeLoginMethod("MANUAL");
          navigate("/login", { replace: true });
          return;
        }

        storeLoginMethod("MYGOV");
        await oidcLogin("MYGOV");
      } catch (err) {
        if (cancelled) return;

        logger.error("[ProtectedRoute] Error checking session status:", err);

        const skipMyGov = await isMyGovSkipEnabled();
        if (cancelled) return;

        if (skipMyGov) {
          navigate("/information", { replace: true });
          return;
        }

        await oidcLogin("MYGOV");
      }
    };

    void checkAuth();
    return cleanup;
  }, [navigate, location.pathname]);

  if (isAuthenticated === null) {
    return <p>{t("protectedRoute.checking")}</p>;
  }

  return isAuthenticated ? <Outlet /> : null;
};

export default ProtectedRoute;
